import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
} from '@mui/material';
import { useState } from 'react';
import { gwtStyles } from '../../config/theme';

/* ── Simple confirmation ── */

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Simple confirm/cancel dialog.
 * Used for: delete study, hold/unhold, etc.
 */
export const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel = 'OK',
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) => (
  <Dialog
    open={open}
    onClose={onCancel}
    PaperProps={{
      sx: {
        minWidth: 340,
        borderRadius: '6px',
        border: `1px solid ${gwtStyles.borderSubtle}`,
        backgroundColor: gwtStyles.popupBg,
      },
    }}
  >
    <DialogTitle sx={{ fontSize: 14, fontWeight: 600, pb: 1 }}>
      {title}
    </DialogTitle>
    <DialogContent>
      <Typography variant="body2" sx={{ fontSize: 13 }}>
        {message}
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 2, pb: 1.5 }}>
      <Button size="small" onClick={onCancel} sx={{ textTransform: 'none', fontSize: 12 }}>
        {cancelLabel}
      </Button>
      <Button
        size="small"
        variant="contained"
        color={destructive ? 'error' : 'primary'}
        onClick={onConfirm}
        sx={{ textTransform: 'none', fontSize: 12 }}
      >
        {confirmLabel}
      </Button>
    </DialogActions>
  </Dialog>
);

/* ── Prompt dialog (with text input) ── */

interface PromptDialogProps {
  open: boolean;
  title: string;
  message: string;
  label?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  required?: boolean;
  onConfirm: (value: string) => void;
  onCancel: () => void;
}

/**
 * Dialog with text input.
 * Used for: hold reason, urgent reason, etc.
 */
export const PromptDialog = ({
  open,
  title,
  message,
  label = '',
  placeholder = '',
  confirmLabel = 'OK',
  cancelLabel = 'Cancel',
  required = false,
  onConfirm,
  onCancel,
}: PromptDialogProps) => {
  const [value, setValue] = useState('');

  const handleConfirm = () => {
    onConfirm(value);
    setValue('');
  };

  const handleCancel = () => {
    setValue('');
    onCancel();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      PaperProps={{
        sx: {
          minWidth: 380,
          borderRadius: '6px',
          border: `1px solid ${gwtStyles.borderSubtle}`,
          backgroundColor: gwtStyles.popupBg,
        },
      }}
    >
      <DialogTitle sx={{ fontSize: 14, fontWeight: 600, pb: 1 }}>
        {title}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ fontSize: 13, mb: 1.5 }}>
          {message}
        </Typography>
        <TextField
          fullWidth
          size="small"
          label={label}
          placeholder={placeholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
          sx={{
            '& .MuiInputBase-root': { fontSize: 13 },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button size="small" onClick={handleCancel} sx={{ textTransform: 'none', fontSize: 12 }}>
          {cancelLabel}
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={required && !value.trim()}
          onClick={handleConfirm}
          sx={{ textTransform: 'none', fontSize: 12 }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/* ── Info dialog ── */

interface InfoDialogProps {
  open: boolean;
  title: string;
  content: React.ReactNode;
  onClose: () => void;
}

/**
 * Read-only info dialog.
 * Used for: study log, DICOM dump, HL7 dump, etc.
 */
export const InfoDialog = ({ open, title, content, onClose }: InfoDialogProps) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="md"
    fullWidth
    PaperProps={{
      sx: {
        borderRadius: '6px',
        border: `1px solid ${gwtStyles.borderSubtle}`,
        backgroundColor: gwtStyles.popupBg,
      },
    }}
  >
    <DialogTitle
      sx={{
        fontSize: 14,
        fontWeight: 600,
        pb: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      {title}
      <Button size="small" onClick={onClose} sx={{ textTransform: 'none', fontSize: 12 }}>
        Close
      </Button>
    </DialogTitle>
    <DialogContent>
      <Box sx={{ fontSize: 12, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
        {content}
      </Box>
    </DialogContent>
  </Dialog>
);
